/**
 * Error Type Mapper
 *
 * This module provides utilities for categorizing errors and determining
 * retry behavior across the application. It serves as the single source
 * of truth for error type detection.
 *
 * @module errorTypeMapper
 * @see {@link ./errorPatternMatcher.js} for pattern definitions
 * @see {@link ../components/AIErrorDisplay.jsx} for error display component
 *
 * @example
 * // Basic usage
 * import { getErrorType, isRetryableError } from "./errorTypeMapper";
 *
 * try {
 *   await fetchData();
 * } catch (error) {
 *   const type = getErrorType(error);
 *   if (isRetryableError(error)) {
 *     // Implement retry logic
 *   }
 * }
 */

import { getErrorInfo } from "./errorPatternMatcher";

/**
 * Map error pattern IDs to AIErrorDisplay error types
 * @type {Object.<string, string>}
 */
export const ERROR_TYPE_MAP = {
  timeout: "network",
  server_error_500: "network",
  server_error_502: "network",
  server_error_503: "network",
  server_error_504: "network",
  authentication: "network", // Backend not configured - maps to network for user-friendly messaging
  rate_limit: "rate_limit",
  content_policy: "safety",
  network: "network",
  model_not_found: "general",
  quota: "rate_limit",
  context_length: "general",
  // New patterns
  connection_reset: "network",
  host_unreachable: "network",
  dns_failure: "network",
  fetch_failed: "network",
};

/**
 * Patterns that indicate transient errors which can be retried
 * @type {string[]}
 */
export const RETRYABLE_PATTERNS = [
  "timeout",
  "server_error_500",
  "server_error_502",
  "server_error_503",
  "server_error_504",
  "network",
  "connection_reset",
  "host_unreachable",
  "dns_failure",
  "fetch_failed",
];

/**
 * Determine AIErrorDisplay error type from error message
 * @param {Error|string} error - Error object or message
 * @returns {string} - Error type for AIErrorDisplay ('network', 'rate_limit', 'safety', 'general')
 */
export function getErrorType(error) {
  const message = typeof error === "string" ? error : error?.message || "";
  const { patternId } = getErrorInfo(message);

  return ERROR_TYPE_MAP[patternId] || "general";
}

/**
 * Check if an error is retryable (transient errors that may succeed on retry)
 * @param {Error|string} error - Error object or message
 * @returns {boolean} - True if error should be retried
 */
export function isRetryableError(error) {
  const message = typeof error === "string" ? error : error?.message || "";
  const { patternId } = getErrorInfo(message);

  return RETRYABLE_PATTERNS.includes(patternId);
}

/**
 * Check if the user appears to be offline
 * Note: This is a simple check using navigator.onLine and may not be 100% accurate
 * @returns {boolean} - True if user appears to be offline
 */
export function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * User-friendly error message variants for the Claude pattern display.
 * Multiple variants per error type to avoid repetitive messaging.
 * @type {Object.<string, string[]>}
 */
const USER_FRIENDLY_MESSAGES = {
  network: [
    "I wasn't able to complete this response. Please check your connection and try again.",
    "Something went wrong on my end. Please try again.",
    "I ran into a connection issue. Please try again in a moment.",
    "I couldn't reach the server. Please check your connection and try again.",
    "The connection was interrupted. Please try again.",
    "I'm having trouble connecting right now. Please try again shortly.",
    "There was a problem completing your request. Please try again.",
    "I lost the connection mid-response. Please try again.",
  ],
  rate_limit: [
    "I'm receiving too many requests right now. Please wait a moment and try again.",
    "I need a short break. Please try again in a minute or two.",
    "Too many requests at once. Please wait a moment before trying again.",
    "I'm a bit overwhelmed right now. Please try again shortly.",
    "Please slow down a bit. Try again in a moment.",
    "I'm processing a lot of requests. Please wait a moment and try again.",
    "High traffic right now. Please try again in a minute.",
  ],
  safety: [
    "I can't help with that request. Please try rephrasing your question.",
    "I'm not able to assist with that. Could you try asking differently?",
    "I can't respond to that request. Please try a different approach.",
    "That's outside what I can help with. Please try rephrasing.",
    "I'm unable to help with that particular request. Please try something else.",
    "I can't assist with that. Please try asking in a different way.",
  ],
  general: [
    "I wasn't able to complete this response. Please try again.",
    "Something unexpected happened. Please try again.",
    "I ran into an issue. Please try again.",
    "That didn't work as expected. Please try again.",
    "I couldn't complete that request. Please try again.",
    "Something went wrong. Please try again.",
    "There was an unexpected problem. Please try again.",
    "I encountered an issue. Please try again.",
  ],
};

/**
 * Track the last message shown per error type to avoid immediate repeats.
 * @type {Object.<string, string>}
 */
const lastShownMessage = {};

/**
 * Cache for error messages to prevent cycling during retries.
 * Maps error message strings to their friendly message + timestamp.
 * Cache entries expire after 10 seconds.
 * @type {Map<string, {message: string, timestamp: number}>}
 */
const errorMessageCache = new Map();
const CACHE_TTL_MS = 10000; // 10 seconds

/**
 * Get a random user-friendly error message for the Claude pattern display.
 * Ensures the same message isn't shown twice in a row for the same error type.
 * Caches results for 10 seconds to prevent cycling during automatic retries.
 * @param {Error|string} error - Error object or message
 * @returns {string} - User-friendly error message
 */
export function getUserFriendlyErrorMessage(error) {
  const rawMessage = typeof error === "string" ? error : error?.message || "";
  const now = Date.now();

  // Check cache first - return same message for same error within TTL
  const cached = errorMessageCache.get(rawMessage);
  if (cached && now - cached.timestamp < CACHE_TTL_MS) {
    return cached.message;
  }

  const errorType = getErrorType(error);
  const messages =
    USER_FRIENDLY_MESSAGES[errorType] || USER_FRIENDLY_MESSAGES.general;

  // If only one message, just return it
  if (messages.length === 1) {
    errorMessageCache.set(rawMessage, { message: messages[0], timestamp: now });
    return messages[0];
  }

  // Filter out the last shown message for this error type
  const lastMessage = lastShownMessage[errorType];
  const availableMessages = lastMessage
    ? messages.filter((msg) => msg !== lastMessage)
    : messages;

  // Pick a random message from available options
  const randomIndex = Math.floor(Math.random() * availableMessages.length);
  const selectedMessage = availableMessages[randomIndex];

  // Store this as the last shown message for this error type
  lastShownMessage[errorType] = selectedMessage;

  // Cache the result to prevent cycling during retries
  errorMessageCache.set(rawMessage, { message: selectedMessage, timestamp: now });

  // Clean up old cache entries periodically
  if (errorMessageCache.size > 50) {
    for (const [key, value] of errorMessageCache.entries()) {
      if (now - value.timestamp > CACHE_TTL_MS) {
        errorMessageCache.delete(key);
      }
    }
  }

  return selectedMessage;
}
